import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getWorkoutLogDetails } from '../lib/database';
import { useSwipeBack } from '../hooks/useSwipeBack';
import './WorkoutLogDetail.css';

function WorkoutLogDetail() {
  const { workoutLogId } = useParams();
  const navigate = useNavigate();
  const [logData, setLogData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { handleTouchStart, handleTouchEnd } = useSwipeBack(() => navigate(-1));

  useEffect(() => {
    if (workoutLogId) {
      loadLogDetails();
    }
  }, [workoutLogId]);

  const loadLogDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getWorkoutLogDetails(workoutLogId);
      setLogData(data);
    } catch (err) {
      console.error('Error loading workout log details:', err);
      setError('Errore nel caricamento del dettaglio');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  const groupExerciseLogs = (exerciseLogs) => {
    const grouped = {};
    exerciseLogs.forEach(log => {
      const key = log.exercise_id === null && log.exercise_template_id
        ? `extra-template-${log.exercise_template_id}`
        : log.exercise_id;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(log);
    });
    return grouped;
  };

  const calculatePerformance = (targetSets, targetReps, actualSets) => {
    if (!actualSets || actualSets.length === 0) {
      return { status: 'incomplete', message: 'Non completato' };
    }

    const completionPercentage = (actualSets.length / targetSets) * 100;

    let minReps, maxReps;
    if (targetReps.includes('-')) {
      const parts = targetReps.split('-');
      minReps = parseInt(parts[0].trim());
      maxReps = parseInt(parts[1].trim());
    } else {
      minReps = maxReps = parseInt(targetReps);
    }

    const allInRange = actualSets.every(set =>
      set.reps >= minReps && set.reps <= maxReps
    );

    if (actualSets.length === targetSets && allInRange) {
      return { status: 'good', message: 'Target raggiunto' };
    } else if (completionPercentage >= 50) {
      return { status: 'ok', message: `${actualSets.length}/${targetSets} serie` };
    } else {
      return { status: 'below', message: `${actualSets.length}/${targetSets} serie` };
    }
  };

  if (loading) {
    return (
      <div className="wld-container" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <header className="wld-header">
          <button className="wld-header-back" onClick={() => navigate(-1)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="wld-header-info">
            <div className="wld-header-date">Caricamento...</div>
          </div>
        </header>
      </div>
    );
  }

  if (error || !logData) {
    return (
      <div className="wld-container" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <header className="wld-header">
          <button className="wld-header-back" onClick={() => navigate(-1)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        </header>
        <main className="wld-main">
          <div className="wld-error">
            {error || 'Workout log non trovato'}
          </div>
        </main>
      </div>
    );
  }

  const groupedLogs = groupExerciseLogs(logData.exercise_logs || []);

  return (
    <div className="wld-container" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {/* Sticky Header */}
      <header className="wld-header">
        <button className="wld-header-back" onClick={() => navigate(-1)} aria-label="Indietro">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div className="wld-header-info">
          <div className="wld-header-date">{formatDate(logData.completed_at)}</div>
          {logData.workout && (
            <h1 className="wld-header-name">{logData.workout.name}</h1>
          )}
        </div>
        {logData.workout && (
          <span className="wld-header-day">G{logData.workout.day_number}</span>
        )}
      </header>

      <main className="wld-main">
        {/* Summary Card */}
        <div className="wld-summary">
          <div className="wld-summary-stats">
            {logData.duration_minutes && (
              <div className="wld-summary-stat">
                <span className="wld-summary-label">Durata</span>
                <span className="wld-summary-value">{logData.duration_minutes}'</span>
              </div>
            )}
            {logData.exercise_logs && (
              <div className="wld-summary-stat">
                <span className="wld-summary-label">Esercizi</span>
                <span className="wld-summary-value">{Object.keys(groupedLogs).length}</span>
              </div>
            )}
          </div>
          {logData.notes && (
            <div className="wld-summary-notes">{logData.notes}</div>
          )}
        </div>

        {/* Exercises */}
        {logData.workout?.exercises && logData.workout.exercises.length > 0 ? (
          <>
            <h2 className="wld-section-title">Esercizi</h2>

            {logData.workout.exercises.map(exercise => {
              const actualSets = groupedLogs[exercise.id] || [];
              const performance = calculatePerformance(exercise.sets, exercise.reps, actualSets);
              const isExtraExercise = exercise.isExtra || (actualSets.length > 0 && actualSets.every(s => s.extra_type === 'extra_exercise'));

              return (
                <div key={exercise.id} className={`wld-exercise ${isExtraExercise ? 'wld-exercise--extra' : ''}`}>
                  <div className="wld-exercise-header">
                    <h3 className="wld-exercise-name">
                      {exercise.name}
                      {isExtraExercise && <span className="wld-badge-extra">EXTRA</span>}
                    </h3>
                    <span className={`wld-badge-perf wld-badge-perf--${performance.status}`}>
                      {performance.message}
                    </span>
                  </div>

                  <div className="wld-exercise-target">
                    Target: {exercise.sets} × {exercise.reps}
                    {exercise.weight ? ` @ ${exercise.weight}kg` : ''}
                  </div>

                  {actualSets.length > 0 ? (
                    <div className="wld-sets">
                      {actualSets
                        .sort((a, b) => a.set_number - b.set_number)
                        .map(set => (
                          <div key={set.id} className={`wld-set ${set.is_extra ? 'wld-set--extra' : ''}`}>
                            <span className="wld-set-num">S{set.set_number}</span>
                            {set.is_extra && <span className="wld-set-badge-extra">+</span>}
                            <span className="wld-set-reps">{set.reps} reps</span>
                            {set.weight > 0 && (
                              <span className="wld-set-weight">{set.weight}kg</span>
                            )}
                            {set.notes && (
                              <span className="wld-set-notes">{set.notes}</span>
                            )}
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="wld-no-sets">Nessuna serie registrata</div>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <div className="wld-empty">
            <p className="wld-empty-title">Nessun esercizio disponibile</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default WorkoutLogDetail;
